/**
 * SaveProgressIndicator - Visual feedback for deal save operations
 *
 * Phase 6 of the Deals Flow Refactoring
 *
 * Shows a minimal, non-intrusive progress indicator during save operations.
 * Displays:
 * - Current step being executed
 * - Progress bar
 * - Success/error state with auto-dismiss
 *
 * @see docs/DEALS_REFACTORING.md for full context
 */

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import ErrorIcon from '@mui/icons-material/Error'
import { Box, Chip, CircularProgress, Fade, LinearProgress, Paper, Stack, Typography, useTheme } from '@mui/material'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import type { SaveProgress, SaveStep } from 'hooks/useSaveDealOrchestrator'
import { useEffect, useState } from 'react'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SaveProgressIndicatorProps {
  /** Current save progress from useSaveDealOrchestrator */
  progress: SaveProgress
  /** Whether we're in edit mode */
  isEditing?: boolean
  /** Whether there are unsaved changes */
  isDirty?: boolean
  /** Number of changed deal fields */
  dealChangesCount?: number
  /** Number of product changes (created + updated + deleted) */
  productChangesCount?: number
  /** Position on screen */
  position?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left'
}

// ─────────────────────────────────────────────────────────────────────────────
// Step display config
// ─────────────────────────────────────────────────────────────────────────────

const STEP_LABELS: Record<SaveStep, string> = {
  idle: '',
  validating: 'Validating...',
  'saving-recipients': 'Updating recipients...',
  'saving-deal': 'Saving deal...',
  'saving-products': 'Syncing products...',
  'updating-pandadoc': 'Updating contract...',
  'updating-cache': 'Finalizing...',
  complete: 'Saved!',
  error: 'Save failed',
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function SaveProgressIndicator({
  progress,
  isEditing = false,
  isDirty = false,
  dealChangesCount = 0,
  productChangesCount = 0,
  position = 'top-right',
}: SaveProgressIndicatorProps) {
  const theme = useTheme()
  const layoutCtx = useLayoutContext()
  const [visible, setVisible] = useState(false)
  const [lastStep, setLastStep] = useState<SaveStep>('idle')

  const isSaving = progress.step !== 'idle' && progress.step !== 'complete' && progress.step !== 'error'
  const isComplete = progress.step === 'complete'
  const isError = progress.step === 'error'
  const showProgress = isSaving || isComplete || isError

  // Auto-hide after success
  useEffect(() => {
    if (isComplete) {
      const timer = setTimeout(() => setVisible(false), 2000)
      return () => clearTimeout(timer)
    }
    if (isError) {
      const timer = setTimeout(() => setVisible(false), 4000)
      return () => clearTimeout(timer)
    }
  }, [isComplete, isError])

  // Show when saving starts
  useEffect(() => {
    if (isSaving) {
      setVisible(true)
      setLastStep(progress.step)
    }
    if (isComplete || isError) {
      setLastStep(progress.step)
    }
  }, [isSaving, isComplete, isError, progress.step])

  // Offset for docked agent panel
  const dockRight = layoutCtx?.isAgentDockOpen && layoutCtx?.agentDock === 'right' ? layoutCtx.agentDockSize + 16 : 16

  // Position styles
  const positionStyles = {
    'top-right': { top: 80, right: dockRight },
    'bottom-right': { bottom: 24, right: dockRight },
    'top-left': { top: 80, left: 16 },
    'bottom-left': { bottom: 24, left: 16 },
  }

  const totalChanges = dealChangesCount + productChangesCount
  return (
    <>
      {/* Edit mode status chip */}
      {isEditing && !showProgress ? (
        <Fade in>
          <Box
            sx={{
              position: 'fixed',
              ...positionStyles[position],
              zIndex: theme => theme.zIndex.snackbar - 1000,
            }}
          >
            <Chip
              icon={<EditIcon sx={{ fontSize: 16 }} />}
              label={isDirty ? `Editing • ${totalChanges} change${totalChanges !== 1 ? 's' : ''}` : 'Editing'}
              size="small"
              color={isDirty ? 'warning' : 'default'}
              sx={{
                fontWeight: 500,
                backdropFilter: 'blur(8px)',
                backgroundColor: isDirty ? 'rgba(237, 108, 2, 0.15)' : 'rgba(255, 255, 255, 0.1)',
                border: '1px solid',
                borderColor: isDirty ? 'warning.main' : 'divider',
              }}
            />
          </Box>
        </Fade>
      ) : (
        <Fade in>
          <Box
            sx={{
              position: 'fixed',
              ...positionStyles[position],
              zIndex: theme => theme.zIndex.snackbar - 1000,
            }}
          >
            <Chip
              label={'Viewing'}
              size="small"
              sx={{
                bgcolor: 'info.main',
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          </Box>
        </Fade>
      )}

      {/* Save progress indicator */}
      <Fade in={visible && showProgress}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            ...positionStyles[position],
            zIndex: theme => theme.zIndex.snackbar - 1000,
            minWidth: 240,
            maxWidth: 320,
            overflow: 'hidden',
            borderRadius: 2,
            backdropFilter: 'blur(12px)',
            backgroundColor: isError
              ? 'rgba(211, 47, 47, 0.95)'
              : isComplete
              ? 'rgba(46, 125, 50, 0.95)'
              : 'rgba(30, 30, 30, 0.95)',
          }}
        >
          {/* Progress bar at top */}
          {isSaving && (
            <LinearProgress
              variant="determinate"
              value={progress.percent}
              sx={{
                height: 3,
                backgroundColor: 'rgba(255,255,255,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'primary.light',
                },
              }}
            />
          )}

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 1.5 }}>
            {/* Icon */}
            {isSaving && <CircularProgress size={20} thickness={4} sx={{ color: 'primary.light' }} />}
            {isComplete && <CheckCircleIcon sx={{ color: 'common.white', fontSize: 22 }} />}
            {isError && <ErrorIcon sx={{ color: 'common.white', fontSize: 22 }} />}

            {/* Text */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'common.white',
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                {STEP_LABELS[lastStep] || progress.message}
              </Typography>
              {isSaving && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {progress.percent}% complete
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>
      </Fade>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Compact variant for inline use
// ─────────────────────────────────────────────────────────────────────────────

export interface SaveStatusBadgeProps {
  progress: SaveProgress
  isEditing?: boolean
  isDirty?: boolean
}

export function SaveStatusBadge({ progress, isEditing, isDirty }: SaveStatusBadgeProps) {
  const isSaving = progress.step !== 'idle' && progress.step !== 'complete' && progress.step !== 'error'
  const isComplete = progress.step === 'complete'
  const isError = progress.step === 'error'

  if (!isEditing && !isSaving && !isComplete && !isError) {
    return null
  }

  let color: 'default' | 'primary' | 'success' | 'error' | 'warning' = 'default'
  let label = ''
  let icon = null

  if (isSaving) {
    color = 'primary'
    label = STEP_LABELS[progress.step]
    icon = <CircularProgress size={12} sx={{ color: 'inherit' }} />
  } else if (isComplete) {
    color = 'success'
    label = 'Saved'
    icon = <CheckCircleIcon sx={{ fontSize: 14 }} />
  } else if (isError) {
    color = 'error'
    label = 'Error'
    icon = <ErrorIcon sx={{ fontSize: 14 }} />
  } else if (isEditing) {
    color = isDirty ? 'warning' : 'default'
    label = isDirty ? 'Unsaved changes' : 'Editing'
    icon = <EditIcon sx={{ fontSize: 14 }} />
  }

  return <Chip icon={icon} label={label} size="small" color={color} sx={{ fontWeight: 500, fontSize: 11 }} />
}

export default SaveProgressIndicator
